import { useCallback, useEffect, useRef, useState } from 'react';

import Button from '@components/common/button';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';

import { apiSandboxService, SandboxApiError } from '@api';
import { useCopyToClipboard } from '@hooks/helpers/useCopyToClipboard';
import { centeredPopupFeatures } from '@utils/connect_helpers';

import IntegratePanel from './IntegratePanel';
import styles from './lottery_connect.module.scss';

type Phase = 'idle' | 'loading' | 'minted' | 'success' | 'cancelled' | 'error';

// Identity/consent sessions post back an authorization `code` (no transaction/amount).
interface IncomingConsentMessage {
    source?: unknown;
    type?: unknown;
    code?: unknown;
}

// Decorative demo draw — sandbox flavour only.
const DRAW_BALLS = [
    { n: 7, tone: 'b1' },
    { n: 14, tone: 'b2' },
    { n: 23, tone: 'b3' },
    { n: 31, tone: 'b4' },
    { n: 42, tone: 'b5' }
];

const CONNECT_STEPS = [
    'Render the Connect button',
    'Customer approves in the popup',
    'Exchange the code for scoped_id'
];

const CONNECT_SNIPPET = `// Load the XEPPT Buttons SDK on your page, then render the Connect button:

xeppt.Buttons({
  // 1 · Create an identity (connect) session on your server
  createSession: () =>
    fetch('/api/connect-session', { method: 'POST' })
      .then((r) => r.json())
      .then((s) => s.connect_token),

  // 2 · Exchange the one-time code server-side for a stable customer id
  onApprove: ({ code }) => {
    fetch('/api/connect/exchange', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },
}).render('#connect-with-xeppt');`;

const LotteryConnect = () => {
    const [phase, setPhase] = useState<Phase>('idle');
    const [code, setCode] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { copied, copy } = useCopyToClipboard();

    const phaseRef = useRef(phase);
    useEffect(() => {
        phaseRef.current = phase;
    }, [phase]);

    const handleMessage = useCallback((event: MessageEvent) => {
        if (phaseRef.current !== 'minted') return;
        if (event.origin !== window.location.origin) return;
        const data = event.data as IncomingConsentMessage;
        if (data?.source !== 'xeppt-connect') return;
        // An identity success carries `code` (a charge carries transaction_id), so a
        // co-mounted checkout popup can never spoof this "you're entered" panel.
        if (data.type === 'success' && typeof data.code === 'string') {
            setCode(data.code);
            setPhase('success');
        } else if (data.type === 'cancel') {
            setPhase('cancelled');
        }
    }, []);

    useEffect(() => {
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [handleMessage]);

    const handleReset = () => {
        setPhase('idle');
        setCode(null);
        setErrorMessage(null);
    };

    const handleConnect = async () => {
        // Open the popup synchronously inside the click gesture — before any await.
        const win = window.open('', 'xeppt_sandbox_signin', centeredPopupFeatures());
        if (!win) {
            setErrorMessage('Popup was blocked. Please allow popups for this site and try again.');
            setPhase('error');
            return;
        }

        setPhase('loading');
        setCode(null);
        setErrorMessage(null);

        try {
            // No amount → identity-only session (scope = identity). No money moves.
            const data = await apiSandboxService.mintSession({});
            setPhase('minted');

            const safePopupPath =
                typeof data.popup_path === 'string' &&
                data.popup_path.startsWith('/') &&
                !data.popup_path.startsWith('//')
                    ? data.popup_path
                    : null;
            if (!win.closed && safePopupPath) {
                // Route to the sandbox popup so the developer's portal session is never
                // disturbed — /connect-sandbox uses in-memory auth only.
                const sandboxPath = safePopupPath.replace(/^\/connect\b/, '/connect-sandbox');
                win.location.href = `${window.location.origin}${sandboxPath}`;
            } else if (!win.closed) {
                win.close();
                setErrorMessage('Unexpected sandbox response — could not open the connect popup.');
                setPhase('error');
            }
        } catch (err) {
            if (!win.closed) win.close();
            const apiErr = err instanceof SandboxApiError ? err : null;
            const errCode = apiErr?.code;
            const msg = apiErr?.message ?? 'Unexpected error — could not mint connect session.';
            setErrorMessage(errCode ? `[${errCode}] ${msg}` : msg);
            setPhase('error');
        }
    };

    const isLoading = phase === 'loading';

    return (
        <div className={styles.section}>
            <div className={styles.section_header}>
                <Typography variant="h5">Connect with XEPPT</Typography>
                <Typography variant="body3" className={styles.section_hint}>
                    A demo lottery site that lets customers connect their XEPPT account to enter.
                    Click <b>Connect with XEPPT</b>, approve in the popup, and you get a one-time code
                    to exchange for their stable customer ID. Runs in test mode — no money moves.
                </Typography>
            </div>

            {/* ── Lottery hero ────────────────────────────────────────── */}
            <div className={styles.stage}>
                <div className={styles.hero}>
                    <div className={styles.hero_glow} />
                    <div className={styles.hero_content}>
                        <span className={styles.brand_tag}>🎟️ XEPPT MILLIONS · Sandbox draw</span>
                        <p className={styles.jackpot_label}>This week&rsquo;s jackpot</p>
                        <p className={styles.jackpot}>$50,000,000</p>

                        <div className={styles.balls}>
                            {DRAW_BALLS.map((b) => (
                                <span
                                    key={b.n}
                                    className={[styles.ball, styles[b.tone]].join(' ')}>
                                    {b.n}
                                </span>
                            ))}
                            <span className={[styles.ball, styles.bonus].join(' ')}>★</span>
                        </div>

                        <div className={styles.countdown}>
                            <Icon name="clock" width={14} height={14} />
                            Draw closes Friday · 9:00 PM
                        </div>
                    </div>
                </div>

                {/* ── Connect / entry card ────────────────────────────── */}
                <div className={styles.entry}>
                    {phase !== 'success' ? (
                        <>
                            <div className={styles.entry_head}>
                                <span className={styles.ticket}>🍀</span>
                                <div>
                                    <p className={styles.entry_title}>Enter this week&rsquo;s draw</p>
                                    <p className={styles.entry_sub}>
                                        Connect your XEPPT account to claim your entry — one tap, no
                                        password.
                                    </p>
                                </div>
                            </div>

                            <Button
                                variant="gold"
                                size="large"
                                full
                                leftIcon="identity"
                                isLoading={isLoading}
                                disabled={isLoading}
                                onClick={() => void handleConnect()}>
                                Connect with XEPPT
                            </Button>

                            <p className={styles.entry_note}>
                                <Icon name="lock" width={12} height={12} />
                                Test mode — you&rsquo;re not really entering a lottery.
                            </p>

                            {phase === 'minted' && (
                                <div className={styles.status_inline}>
                                    <Icon
                                        name="loading_spinner"
                                        width={18}
                                        height={18}
                                        className={styles.spinner}
                                    />
                                    <Typography variant="body4">
                                        Popup opened — waiting for the customer to connect…
                                    </Typography>
                                </div>
                            )}
                            {phase === 'cancelled' && (
                                <div className={styles.status_inline}>
                                    <Icon
                                        name="close_outlined"
                                        width={18}
                                        height={18}
                                        className={styles.icon_cancelled}
                                    />
                                    <Typography variant="body4">
                                        Connection was cancelled by the customer.
                                    </Typography>
                                </div>
                            )}
                            {phase === 'error' && (
                                <div className={styles.status_inline}>
                                    <Icon
                                        name="error_outlined"
                                        width={18}
                                        height={18}
                                        className={styles.icon_error}
                                    />
                                    <Typography variant="body4">
                                        {errorMessage ?? 'Failed to mint connect session.'}
                                    </Typography>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={styles.won}>
                            <span className={styles.won_emoji}>🎉</span>
                            <p className={styles.won_title}>You&rsquo;re entered!</p>
                            <p className={styles.won_sub}>
                                The customer connected their XEPPT account. Exchange this one-time
                                code server-side for their stable&nbsp;<code>scoped_id</code>.
                            </p>
                            <div className={styles.code_box}>
                                <span className={styles.code_label}>authorization code</span>
                                <span className={styles.code_value}>{code}</span>
                                <button
                                    className={styles.copy_btn}
                                    onClick={() => code && void copy(code)}
                                    type="button"
                                    aria-label="Copy authorization code">
                                    <Icon
                                        name={copied ? 'filled_check' : 'copy'}
                                        width={14}
                                        height={14}
                                    />
                                    {copied ? 'Copied!' : 'Copy'}
                                </button>
                            </div>
                            <p className={styles.exchange_note}>
                                POST&nbsp;/v1/connect/exchange — the code expires shortly and can be
                                used once.
                            </p>
                            <button className={styles.reset_btn} onClick={handleReset} type="button">
                                <Icon name="reload" width={14} height={14} />
                                Run again
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <IntegratePanel
                steps={CONNECT_STEPS}
                code={CONNECT_SNIPPET}
                docHref="https://docs.xeppt.com"
            />
        </div>
    );
};

export default LotteryConnect;
